// White-label / Personalização por tenant (Pacote 2, itens 1 e 2).
// Conversão hex -> HSL "H S% L%" porque as variáveis CSS do projeto já usam
// esse formato (tailwind.config.js mapeia as cores para hsl(var(--x))) — ver
// index.css. Nenhuma dependência nova: é só um cálculo de cor padrão.

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export function hexToHslTriplet(hex) {
  if (!hex || !HEX_RE.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Contraste automático: escolhe texto claro ou escuro sobre a cor de fundo
// informada, por luminância relativa simplificada — "texto sobre cores deve
// escolher automaticamente uma cor legível" (item 2).
export function readableTextHex(hex) {
  if (!hex || !HEX_RE.test(hex)) return "#FFFFFF";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#0F172A" : "#FFFFFF";
}

export function isValidHex(hex) {
  return HEX_RE.test(hex || "");
}

function setOrClear(root, name, hex) {
  const triplet = hexToHslTriplet(hex);
  if (triplet) root.style.setProperty(name, triplet);
  else root.style.removeProperty(name);
}

function hueOf(hex) {
  const t = hexToHslTriplet(hex);
  return t ? parseInt(t.split(" ")[0], 10) : null;
}

// Fundo apropriado (item 3 do Pacote de Correções): tema_fundo é um
// conjunto FECHADO (claro/neutro/suave), nunca uma cor de fundo livre.
// Calcula um par claro/escuro para cada opção — o par certo é escolhido
// pelo CSS (:root vs .dark em index.css), não aqui, porque o tema
// claro/escuro (item 4) é uma preferência do USUÁRIO, independente da
// Personalização do tenant; um mesmo tenant "suave" precisa continuar
// coerente tanto em modo claro quanto escuro.
function computeBgVars(temaFundo, corPrincipal) {
  if (temaFundo === "neutro") return { light: "0 0% 100%", dark: "0 0% 7%" };
  if (temaFundo === "suave") {
    const h = hueOf(corPrincipal);
    if (h != null) return { light: `${h} 45% 97%`, dark: `${h} 30% 9%` };
  }
  return { light: null, dark: null }; // "claro" (padrão) — usa os valores fixos de index.css
}

// Aplica a personalização do tenant nas variáveis CSS globais — sem alterar
// nenhum arquivo/CSS estático (reflete no ambiente sem alteração de código,
// item 1). Chamado uma vez no Layout ao carregar e de novo sempre que a
// aba Personalização salva com sucesso.
export function applyBrandingVars(branding) {
  const root = document.documentElement;
  setOrClear(root, "--brand-primary", branding?.cor_principal);
  setOrClear(root, "--brand-primary-fg", branding?.cor_principal ? readableTextHex(branding.cor_principal) : null);
  setOrClear(root, "--brand-accent", branding?.cor_destaque);
  setOrClear(root, "--brand-accent-fg", branding?.cor_destaque ? readableTextHex(branding.cor_destaque) : null);
  setOrClear(root, "--brand-support", branding?.cor_apoio);
  setOrClear(root, "--brand-support-fg", branding?.cor_apoio ? readableTextHex(branding.cor_apoio) : null);
  // Propagação para os tokens shadcn (--primary/--ring) já usados por
  // componentes como <Button>/<Tooltip> — só quando cor_principal foi
  // personalizada; sem isso, os componentes que já leem --primary
  // continuam no azul padrão do LOOTUS automaticamente.
  if (branding?.cor_principal) {
    setOrClear(root, "--primary", branding.cor_principal);
    setOrClear(root, "--ring", branding.cor_principal);
    setOrClear(root, "--primary-foreground", readableTextHex(branding.cor_principal));
  } else {
    root.style.removeProperty("--primary");
    root.style.removeProperty("--ring");
    root.style.removeProperty("--primary-foreground");
  }
  const bg = computeBgVars(branding?.tema_fundo, branding?.cor_principal);
  if (bg.light) root.style.setProperty("--brand-bg-light", bg.light); else root.style.removeProperty("--brand-bg-light");
  if (bg.dark) root.style.setProperty("--brand-bg-dark", bg.dark); else root.style.removeProperty("--brand-bg-dark");
}

export const displayName = (c) => (c?.nome_fantasia && c.nome_fantasia.trim()) || (c?.razao_social && c.razao_social.trim()) || "LOOTUS";
export const initials = (name) => (name || "L").trim().slice(0, 1).toUpperCase();

// Exportado para a pré-visualização de Configuracoes.jsx usar EXATAMENTE o
// mesmo cálculo de fundo do ambiente real ("a pré-visualização deve
// refletir as mesmas regras utilizadas pelo ambiente real" — Correções,
// item 3), em vez de reimplementar a lógica de tema_fundo separadamente.
export { computeBgVars };
export const hslTripletToCss = (triplet) => (triplet ? `hsl(${triplet})` : null);
